import React, { useEffect, useState } from "react";
import "./survey.scss";
import { saveSurvey } from "src/redux/slices/surveySlice";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { surveySchema } from "src/schemas/surveySchema";
import { surveyInitialState } from "src/state/surveyInitialState";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "primereact/dropdown";
import { getFieldName, isRtl, scrollToTopWithClass } from "src/utils/helpers";
import { IEmirate } from "src/redux/interfaces/ILookup_1";
import { toast } from "react-toastify";
import { RoutePaths } from "src/resources/enum";
import { useNavigate } from "react-router";
import {
  getServiceTerritories,
  setServiceTerritories,
} from "src/redux/slices/lookupSlice";
import _ from "lodash";
import AssignBuildingTitle from "src/components/AssignBuilding/AssignBuildingTitle";
import { LG_TEXT_FIELD_SIZE, MD_TEXT_FIELD_SIZE } from "src/resources/constant";
const Survey = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isSubmitButtonClicked, setIsSubmitButtonClicked] =
    useState<any>(false);
  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);
  const emirateOptions = useSelector((state: any) =>
    state.lookup.emiratesOptions.filter(
      (em: IEmirate) => em.emirateCode === "AUH"
    )
  );

  const serviceTerritoryOptions = useSelector(
    (state: any) => state.lookup.serviceTerritories
  );

  const { t } = useTranslation();
  const language = useSelector((state: any) => state.lookup.language);

  const [surveyDetails, setSurveyDetails] = useState(surveyInitialState);
  const [errors, setErrors] = useState(surveyInitialState);

  const validateSurveyForm = (surveyDetails: any) => {
    const { error } = surveySchema(surveyDetails).validate(surveyDetails, {
      abortEarly: false,
    });
    if (error) {
      const newErrors: any = {};
      error.details.forEach((err: any) => {
        newErrors[err.context?.key] = err.message;
      });
      setErrors(newErrors);
      return false;
    }
    setErrors(surveyInitialState);
    return true;
  };

  const handleSubmit = (event: any): void => {
    event.preventDefault();
    setIsSubmitButtonClicked(true);
    if (!validateSurveyForm(surveyDetails)) {
      scrollToTopWithClass("invalid-input", null);
      return;
    }
    dispatch(
      saveSurvey(surveyDetails, () => {
        toast.success(t("surveySavedSuccessfully"));
        setSurveyDetails(surveyInitialState);
        navigate(RoutePaths.AssignedBuildings);
      }) as any
    );
  };

  useEffect(() => {
    if (!_.isEqual(errors, surveyInitialState)) {
      validateSurveyForm(surveyDetails);
    }
  }, [language]);

  const handleInputChange = (e: any): void => {
    const { name, value } = e.target;
    setSurveyDetails({ ...surveyDetails, [name]: value });
  };

  const handleCityChange = (e: any) => {
    let name, value;
    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }
    dispatch(setServiceTerritories([]));
    setSurveyDetails({ ...surveyDetails, [name]: value });
    dispatch(getServiceTerritories(false, value) as any);
  };

  useEffect(() => {
    if (isSubmitButtonClicked) validateSurveyForm(surveyDetails);
  }, [surveyDetails, isSubmitButtonClicked]);

  return (
    <>
      <AssignBuildingTitle />
      <div
        className={`registration-form-wrapper survey-form-wrapper ps-0 mt-4 edit-popup-wrapper"`}
      >
        <div className="registration-form pt-3">
          <h2 className="text-center mb-3">{t("addBuildingSurvey")}</h2>
          <form onSubmit={handleSubmit}>
            <div className="section">
              <h3> {t("propertyAddress")}</h3>
              <div className="form-group-row mb-0">
                <div className="form-group">
                  <label htmlFor="propertyName" className="required">
                    {t("propertyName")}
                  </label>
                  <input
                    placeholder={t("enter") + " " + t("propertyName")}
                    type="text"
                    value={surveyDetails.propertyName}
                    id="propertyName"
                    name="propertyName"
                    onChange={handleInputChange}
                    required
                    className={`${errors.propertyName ? "invalid-input" : ""}`}
                  />
                  {errors.propertyName && (
                    <p className="error">{errors.propertyName}</p>
                  )}
                </div>
                <div className="form-group"></div>
                <div className="form-group">
                  <label htmlFor="address" className="required">
                    {t("address")}
                  </label>
                  <input
                    placeholder={t("enter") + " " + t("address")}
                    type="text"
                    value={surveyDetails.address}
                    id="address"
                    name="address"
                    onChange={handleInputChange}
                    required
                    className={`${errors.address ? "invalid-input" : ""}`}
                  />
                  {errors.address && <p className="error">{errors.address}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="emirate" className="required">
                    {t("emirate")}
                  </label>
                  <Dropdown
                    id="emirate"
                    value={surveyDetails.emirate}
                    name="emirate"
                    options={emirateOptions}
                    optionLabel={getFieldName(language)}
                    optionValue="emirateId"
                    onChange={handleInputChange}
                    placeholder={t("selectAnEmirate")}
                    required
                    className={`${errors.emirate ? "invalid-input" : ""}`}
                    emptyMessage={t("noResultsFound")}
                    emptyFilterMessage={t("noResultsFound")}
                  />
                  {errors.emirate && <p className="error">{errors.emirate}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="city" className="required">
                    {t("city")} - {t("selectEmirateFirst")}
                  </label>
                  <Dropdown
                    id="city"
                    value={surveyDetails.city}
                    name="city"
                    options={surveyDetails.emirate ? citiesOptions : []}
                    optionLabel={getFieldName(language)}
                    optionValue="cityId"
                    onChange={handleCityChange}
                    required
                    placeholder={t("selectCity")}
                    className={`${errors.city ? "invalid-input" : ""}`}
                    emptyMessage={t("noResultsFound")}
                    emptyFilterMessage={t("noResultsFound")}
                  />
                  {errors.city && <p className="error">{errors.city}</p>}
                </div>

                <div className="form-group">
                  <label htmlFor="territory" className="required">
                    {t("territory")} - {t("selectCityFirst")}
                  </label>
                  <Dropdown
                    options={surveyDetails.city ? serviceTerritoryOptions : []}
                    placeholder={t("serviceTerritory")}
                    id="territory"
                    optionValue="territoryId"
                    optionLabel={getFieldName(language)}
                    panelClassName="status-dropdown-panel"
                    onChange={handleInputChange}
                    value={surveyDetails.territory}
                    name="territory"
                    required
                    className={`${errors.territory ? "invalid-input" : ""}`}
                    emptyMessage={t("noResultsFound")}
                    emptyFilterMessage={t("noResultsFound")}
                  />
                  {errors.territory && (
                    <p className="error">{errors.territory}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="latitude" className="required">
                    {t("latitude")}
                  </label>
                  <input
                    placeholder={t("enter") + " " + t("latitude")}
                    type="text"
                    value={surveyDetails.latitude}
                    id="latitude"
                    name="latitude"
                    onChange={handleInputChange}
                    required
                    className={`${errors.latitude ? "invalid-input" : ""}`}
                  />
                  {errors.latitude && (
                    <p className="error">{errors.latitude}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="longitude" className="required">
                    {t("longitude")}
                  </label>

                  <input
                    placeholder={t("enter") + " " + t("longitude")}
                    type="text"
                    id="longitude"
                    value={surveyDetails.longitude}
                    name="longitude"
                    onChange={handleInputChange}
                    required
                    className={`${errors.longitude ? "invalid-input" : ""}`}
                  />
                  {errors.longitude && (
                    <p className="error">{errors.longitude}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="section">
              <h3>{t("contactDetails")}</h3>
              <div className="form-group-row mb-0">
                <div className="form-group">
                  <label htmlFor="accountHolderFirstName" className="required">
                    {t("accountHolderFirstName")}
                  </label>

                  <input
                    type="text"
                    placeholder={t("enter") + " " + t("accountHolderFirstName")}
                    id="accountHolderFirstName"
                    value={surveyDetails.accountHolderFirstName}
                    name="accountHolderFirstName"
                    onChange={handleInputChange}
                    required
                    className={`${
                      errors.accountHolderFirstName ? "invalid-input" : ""
                    }`}
                  />
                  {errors.accountHolderFirstName && (
                    <p className="error">{errors.accountHolderFirstName}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="accountHolderLastName" className="required">
                    {t("accountHolderLastName")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("enter") + " " + t("accountHolderLastName")}
                    value={surveyDetails.accountHolderLastName}
                    id="accountHolderLastName"
                    name="accountHolderLastName"
                    onChange={handleInputChange}
                    required
                    className={`${
                      errors.accountHolderLastName ? "invalid-input" : ""
                    }`}
                  />
                  {errors.accountHolderLastName && (
                    <p className="error">{errors.accountHolderLastName}</p>
                  )}
                </div>

                <div className="form-group">
                  <label
                    htmlFor="accountHolderMobileNumber"
                    className="required"
                  >
                    {t("accountHolderMobileNumber")}
                  </label>
                  <div className="form-group input-with-label">
                    <span className="ltr">+971</span>
                    <input
                      value={surveyDetails.accountHolderMobileNumber}
                      placeholder={
                        t("enter") + " " + t("accountHolderMobileNumber")
                      }
                      type="text"
                      id="accountHolderMobileNumber"
                      name="accountHolderMobileNumber"
                      onChange={handleInputChange}
                      required
                      className={`${
                        errors.accountHolderMobileNumber ? "invalid-input" : ""
                      }`}
                    />
                  </div>
                  {errors.accountHolderMobileNumber && (
                    <p className="error mobile-error">
                      {errors.accountHolderMobileNumber}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="emirateNumber" className="required">
                    {t("emirateNumber")}
                  </label>
                  <input
                    value={surveyDetails.emirateNumber}
                    placeholder={t("enter") + " " + t("emirateNumber")}
                    type="text"
                    id="emirateNumber"
                    name="emirateNumber"
                    onChange={handleInputChange}
                    required
                    className={`${errors.emirateNumber ? "invalid-input" : ""}`}
                  />
                  {errors.emirateNumber && (
                    <p className="error">{errors.emirateNumber}</p>
                  )}
                </div>
                <div className="form-group">
                  <label
                    htmlFor="accountHolderEmailAddress"
                    className="required"
                  >
                    {t("accountHolderEmailAddress")}
                  </label>
                  <input
                    value={surveyDetails.accountHolderEmailAddress}
                    placeholder={
                      t("enter") + " " + t("accountHolderEmailAddress")
                    }
                    type="text"
                    id="accountHolderEmailAddress"
                    name="accountHolderEmailAddress"
                    onChange={handleInputChange}
                    required
                    className={`${
                      errors.accountHolderEmailAddress ? "invalid-input" : ""
                    }`}
                  />
                  {errors.accountHolderEmailAddress && (
                    <p className="error">{errors.accountHolderEmailAddress}</p>
                  )}
                </div>

                <div className="form-group">
                  <label
                    htmlFor="surveyContactPersonFirstName"
                    className="required"
                  >
                    {t("surveyContactPersonFirstName")}
                  </label>
                  <input
                    value={surveyDetails.surveyContactPersonFirstName}
                    placeholder={
                      t("enter") + " " + t("surveyContactPersonFirstName")
                    }
                    type="text"
                    id="surveyContactPersonFirstName"
                    name="surveyContactPersonFirstName"
                    onChange={handleInputChange}
                    required
                    className={`${
                      errors.surveyContactPersonFirstName ? "invalid-input" : ""
                    }`}
                  />
                  {errors.surveyContactPersonFirstName && (
                    <p className="error">
                      {errors.surveyContactPersonFirstName}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label
                    htmlFor="surveyContactPersonLastName"
                    className="required"
                  >
                    {t("surveyContactPersonLastName")}
                  </label>

                  <input
                    type="text"
                    placeholder={
                      t("enter") + " " + t("surveyContactPersonLastName")
                    }
                    id="surveyContactPersonLastName"
                    value={surveyDetails.surveyContactPersonLastName}
                    name="surveyContactPersonLastName"
                    onChange={handleInputChange}
                    required
                    className={`${
                      errors.surveyContactPersonLastName ? "invalid-input" : ""
                    }`}
                  />
                  {errors.surveyContactPersonLastName && (
                    <p className="error">
                      {errors.surveyContactPersonLastName}
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label
                    htmlFor="surveyContactPersonMobileNumber"
                    className="required"
                  >
                    {t("surveyContactPersonMobileNumber")}
                  </label>
                  <div className="form-group input-with-label">
                    <span className="ltr">+971</span>
                    <input
                      type="text"
                      placeholder={
                        t("enter") + " " + t("surveyContactPersonMobileNumber")
                      }
                      value={surveyDetails.surveyContactPersonMobileNumber}
                      id="surveyContactPersonMobileNumber"
                      name="surveyContactPersonMobileNumber"
                      onChange={handleInputChange}
                      required
                      className={`${
                        errors.surveyContactPersonMobileNumber
                          ? "invalid-input"
                          : ""
                      }`}
                    />
                  </div>
                  {errors.surveyContactPersonMobileNumber && (
                    <p className="error mobile-error">
                      {errors.surveyContactPersonMobileNumber}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label
                    htmlFor="emergencyContact1FirstName"
                    className="required"
                  >
                    {t("emergencyContact1FirstName")}
                  </label>
                  <input
                    value={surveyDetails.emergencyContact1FirstName}
                    placeholder={
                      t("enter") + " " + t("emergencyContact1FirstName")
                    }
                    type="text"
                    id="emergencyContact1FirstName"
                    name="emergencyContact1FirstName"
                    onChange={handleInputChange}
                    required
                    className={`${
                      errors.emergencyContact1FirstName ? "invalid-input" : ""
                    }`}
                  />
                  {errors.emergencyContact1FirstName && (
                    <p className="error">{errors.emergencyContact1FirstName}</p>
                  )}
                </div>

                <div className="form-group">
                  <label
                    htmlFor="emergencyContact1MobileNumber"
                    className="required"
                  >
                    {t("emergencyContact1MobileNumber")}
                  </label>
                  <div className="form-group input-with-label">
                    <span className="ltr">+971</span>
                    <input
                      value={surveyDetails.emergencyContact1MobileNumber}
                      placeholder={
                        t("enter") + " " + t("emergencyContact1MobileNumber")
                      }
                      type="text"
                      id="emergencyContact1MobileNumber"
                      name="emergencyContact1MobileNumber"
                      onChange={handleInputChange}
                      required
                      className={`${
                        errors.emergencyContact1MobileNumber
                          ? "invalid-input"
                          : ""
                      }`}
                    />
                  </div>
                  {errors.emergencyContact1MobileNumber && (
                    <p className="error mobile-error">
                      {errors.emergencyContact1MobileNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {
              <div className="d-flex">
                <Button
                  type="submit"
                  className="primary-btn submit-button"
                  onClick={handleSubmit}
                >
                  {t("submit")}
                </Button>
              </div>
            }
          </form>
        </div>
      </div>
    </>
  );
};

export default Survey;
